import { deepRemoveUndefinedProperties } from '../utils/deep-remove-undefined-properties';
import { insertSectionIntoTree } from '../utils/page/section';
export const emptyDraftState = () => ({
    pages: {},
    site: { siteMetadata: {} },
    widgets: []
});
export const draftDataToState = (data) => {
    const state = emptyDraftState();
    for (const page of data?.pages || []) {
        if (!page || !page.id)
            continue;
        state.pages[page.id] = page;
    }
    if (data?.site) {
        state.site = { siteMetadata: data.site.siteMetadata || {} };
    }
    state.widgets = data?.widgets || [];
    return state;
};
export const draftStateToData = (state) => ({
    pages: Object.entries(state.pages).map(([id, page]) => ({ ...page, id })),
    site: state.site,
    widgets: state.widgets
});
const pathKey = (path) => (path || [])
    .map(entry => [entry.fieldName, entry.sectionId].filter(Boolean).join('#'))
    .join('/');
export const changeKey = (change) => {
    switch (change.kind) {
        case 'fieldWrite':
        case 'fieldMerge': {
            const inSection = change.section
                ? `${pathKey(change.section.path)}/${change.section.id ?? ''}/`
                : '';
            return `${change.pageId ?? ''}/${inSection}${change.fieldType ?? ''}/${change.fieldName ?? ''}`;
        }
        case 'sectionAdd':
        case 'sectionRemove':
        case 'sectionMove':
            return `${change.pageId ?? ''}/sections/${pathKey(change.section?.path)}`;
        case 'pageUpdate':
            return `${change.pageId ?? 'new'}/page`;
        case 'pageDelete':
            return `${change.pageId ?? ''}/deleted`;
        case 'siteMetadata':
            return 'site/siteMetadata';
        case 'widgetWrite':
            return `widget/${change.value?.id ?? 'new'}`;
        default:
            return 'unknown';
    }
};
const touchPage = (state, pageId) => {
    state.pages[pageId] = {
        ...state.pages[pageId],
        childPages: state.pages[pageId]?.childPages || []
    };
    return state.pages[pageId];
};
export const applyChange = (state, change, options) => {
    const at = change.at || new Date().toISOString();
    switch (change.kind) {
        case 'fieldWrite': {
            if (!change.pageId || !change.fieldType || !change.fieldName)
                break;
            const page = touchPage(state, change.pageId);
            page.modifiedAt = at;
            if (change.section?.id) {
                page.sections = page.sections || [];
                insertSectionIntoTree(page.sections, change.section.path, {
                    sectionId: change.section.id,
                    blockData: {
                        jaenFields: {
                            [change.fieldType]: {
                                [change.fieldName]: {
                                    value: change.value,
                                    props: change.props
                                }
                            }
                        }
                    }
                });
            }
            else {
                page.jaenFields = page.jaenFields || {};
                page.jaenFields[change.fieldType] = {
                    ...page.jaenFields[change.fieldType],
                    [change.fieldName]: {
                        ...page.jaenFields[change.fieldType]?.[change.fieldName],
                        value: change.value,
                        props: change.props
                    }
                };
            }
            break;
        }
        case 'fieldMerge': {
            if (!change.pageId || !change.fieldType || !change.fieldName)
                break;
            const page = touchPage(state, change.pageId);
            page.modifiedAt = at;
            page.jaenFields = page.jaenFields || {};
            const field = page.jaenFields[change.fieldType]?.[change.fieldName];
            const previous = field?.value;
            const merged = previous && typeof previous === 'object' && !Array.isArray(previous)
                ? { ...previous }
                : {};
            for (const key of (change.props?.removed || [])) {
                if (typeof key === 'string')
                    delete merged[key];
            }
            if (change.value &&
                typeof change.value === 'object' &&
                !Array.isArray(change.value)) {
                Object.assign(merged, change.value);
            }
            page.jaenFields[change.fieldType] = {
                ...page.jaenFields[change.fieldType],
                [change.fieldName]: { ...field, value: merged }
            };
            break;
        }
        case 'sectionAdd': {
            if (!change.pageId || !change.section)
                break;
            const page = touchPage(state, change.pageId);
            const sections = page.sections || [];
            insertSectionIntoTree(sections, change.section.path, {
                between: change.props?.between,
                blockData: {
                    type: change.props?.sectionItemType
                }
            });
            page.sections = sections;
            break;
        }
        case 'sectionRemove': {
            if (!change.pageId || !change.section?.id)
                break;
            const page = touchPage(state, change.pageId);
            const sections = page.sections || [];
            insertSectionIntoTree(sections, change.section.path, {
                between: change.props?.between,
                sectionId: change.section.id,
                shouldDelete: true
            });
            page.sections = sections;
            break;
        }
        case 'sectionMove': {
            if (!change.pageId || !change.section?.id)
                break;
            const page = touchPage(state, change.pageId);
            const sections = page.sections || [];
            insertSectionIntoTree(sections, change.section.path, {
                between: change.props?.between,
                sectionId: change.section.id,
                move: change.props?.move
            });
            page.sections = sections;
            break;
        }
        case 'pageUpdate': {
            const payload = (change.value || {});
            const id = change.pageId || payload.id;
            if (!id)
                break;
            const { fromId, ...rest } = payload;
            const parentPageId = rest.parentPage?.id || null;
            const exists = state.pages[id] !== undefined;
            if (exists) {
                const toBeAdded = deepRemoveUndefinedProperties({
                    id,
                    modifiedAt: at,
                    ...(rest.slug && { slug: rest.slug }),
                    ...(rest.jaenFields !== undefined && { jaenFields: rest.jaenFields }),
                    ...(rest.sections !== undefined && { sections: rest.sections }),
                    jaenPageMetadata: rest.jaenPageMetadata,
                    ...(rest.parentPage !== undefined && { parentPage: rest.parentPage }),
                    ...(rest.childPages && { childPages: rest.childPages }),
                    ...(rest.excludedFromIndex !== undefined && {
                        excludedFromIndex: rest.excludedFromIndex
                    }),
                    childPagesOrder: rest.childPagesOrder || []
                });
                state.pages[id] = {
                    ...state.pages[id],
                    ...toBeAdded,
                    jaenPageMetadata: {
                        ...state.pages[id]?.jaenPageMetadata,
                        ...rest.jaenPageMetadata
                    }
                };
                if (fromId && (parentPageId || parentPageId === null)) {
                    state.pages[fromId] = {
                        ...state.pages[fromId],
                        modifiedAt: at,
                        childPages: [
                            ...(state.pages[fromId]?.childPages || []).filter(e => e.id !== id),
                            { id, deleted: true }
                        ]
                    };
                }
            }
            else {
                state.pages[id] = {
                    createdAt: at,
                    modifiedAt: at,
                    createdBy: rest.createdBy,
                    slug: rest.slug,
                    jaenFields: rest.jaenFields || null,
                    sections: rest.sections,
                    jaenPageMetadata: rest.jaenPageMetadata || { title: 'New Page' },
                    parentPage: rest.parentPage || null,
                    childPages: rest.childPages || [],
                    template: rest.template,
                    excludedFromIndex: rest.excludedFromIndex,
                    childPagesOrder: rest.childPagesOrder || []
                };
            }
            if (parentPageId) {
                const childPages = state.pages[parentPageId]?.childPages || [{ id }];
                if (!childPages.find(e => e.id === id)) {
                    childPages.push({ id });
                }
                state.pages[parentPageId] = {
                    modifiedAt: at,
                    ...state.pages[parentPageId],
                    childPages
                };
            }
            break;
        }
        case 'pageDelete': {
            if (!change.pageId)
                break;
            state.pages[change.pageId] = {
                ...state.pages[change.pageId],
                deleted: true,
                childPages: state.pages[change.pageId]?.childPages || []
            };
            break;
        }
        case 'siteMetadata': {
            state.site = {
                siteMetadata: {
                    ...state.site?.siteMetadata,
                    ...(change.value || {})
                }
            };
            break;
        }
        case 'widgetWrite': {
            const { id, isCreate, ...data } = (change.value ||
                {});
            if (id) {
                const node = state.widgets.find(node => node.id === id);
                if (node) {
                    node.modifiedAt = at;
                    node.data = data.data;
                    break;
                }
            }
            if (!data.name)
                break;
            const node = { id, ...data, modifiedAt: at };
            if (isCreate && !data.createdAt) {
                node.createdAt = at;
            }
            state.widgets.push(node);
            break;
        }
    }
    if (options?.authors && options.author) {
        options.authors[changeKey(change)] = { ...options.author, at };
    }
    return state;
};
export const applyChanges = (state, changes, options) => {
    for (const change of changes) {
        applyChange(state, change, options);
    }
    return state;
};
export const overwrittenFields = (changes, authors, since) => {
    const seen = new Set();
    const out = [];
    for (const change of changes) {
        const field = changeKey(change);
        if (seen.has(field))
            continue;
        seen.add(field);
        const previous = authors[field];
        if (previous && previous.at > since) {
            out.push({ field, previousAuthor: previous });
        }
    }
    return out;
};
export const MEDIA_FIELD_TYPE = 'IMA:MEDIA_NODES';
const isRecord = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
export const recordDifference = (before, after) => {
    if (!isRecord(before) || !isRecord(after))
        return null;
    const changed = {};
    const removed = [];
    for (const [key, value] of Object.entries(after)) {
        if (JSON.stringify(before[key]) !== JSON.stringify(value)) {
            changed[key] = value;
        }
    }
    for (const key of Object.keys(before)) {
        if (!(key in after))
            removed.push(key);
    }
    const touched = Object.keys(changed).length + removed.length;
    if (touched === 0)
        return { changed, removed };
    if (touched * 2 > Object.keys(before).length + removed.length)
        return null;
    return { changed, removed };
};
